import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import MentorHeader from './MentorHeader';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import VoiceControls from './VoiceControls';
import ChatInput from './ChatInput';
import MentorSettingsModal from './MentorSettingsModal';
import { MdStop } from 'react-icons/md';
import './AIMentorPanel.css';

// Utility to strip markdown specifically for Voice
const stripMarkdownForVoice = (text) => {
  if (!text) return '';
  let cleanText = text;
  cleanText = cleanText.replace(/\\*\\*(.*?)\\*\\*/g, '$1'); // Remove bold
  cleanText = cleanText.replace(/\\*(.*?)\\*/g, '$1');     // Remove italic
  cleanText = cleanText.replace(/#(.*?)\\n/g, '$1\\n');    // Remove markdown headers
  cleanText = cleanText.replace(/\`\`\`[\\s\\S]*?\`\`\`/g, ' Code snippet provided. '); // Don't read big code blocks
  cleanText = cleanText.replace(/\`(.*?)\`/g, '$1');       // Remove inline code ticks
  return cleanText;
};

const AIMentorPanel = ({ onClose, getEditorContext, language, roomId, width }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [readCode, setReadCode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [aiSettings, setAiSettings] = useState(() => {
    const saved = localStorage.getItem('aiMentorSettings');
    return saved ? JSON.parse(saved) : { aiMode: 'cloud', localUrl: 'http://localhost:11434', localModel: 'llama3' };
  });
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const stopGenerating = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  // Fetch chat history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await fetch(`/api/ai/chat/${roomId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.history || []);
        }
      } catch (error) {
        console.error('Failed to load AI chat history:', error);
      }
    };

    if (roomId) {
      fetchHistory();
    }
  }, [roomId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Stop any ongoing speech

      const cleanText = stripMarkdownForVoice(text);
      // Split by paragraphs to add natural pacing
      const paragraphs = cleanText.split('\\n\\n').filter(p => p.trim().length > 0);

      if (paragraphs.length === 0) return;

      setIsSpeaking(true);

      let utterancesCompleted = 0;

      paragraphs.forEach((paragraph, index) => {
        const utterance = new SpeechSynthesisUtterance(paragraph);

        // Add a slight pause between paragraphs by tweaking pitch/rate if needed, 
        // or just rely on queueing
        utterance.rate = 1.0;

        utterance.onend = () => {
          utterancesCompleted++;
          if (utterancesCompleted === paragraphs.length) {
            setIsSpeaking(false);
          }
        };

        utterance.onerror = () => {
          setIsSpeaking(false);
        };

        window.speechSynthesis.speak(utterance);
      });
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const replayLastMessage = () => {
    const aiMessages = messages.filter(m => m.role === 'ai');
    if (aiMessages.length > 0) {
      speakText(aiMessages[aiMessages.length - 1].content);
    }
  };

  const handleSendMessage = async (question) => {
    // Add user message
    const userMsg = { role: 'user', content: question, id: Date.now() };
    const aiMsgId = Date.now() + 1;
    const aiMsg = { role: 'ai', content: '', id: aiMsgId, metadata: null };

    setMessages(prev => [...prev, userMsg, aiMsg]);
    setIsLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const editorContext = getEditorContext();
      const token = localStorage.getItem('token');

      if (aiSettings.aiMode === 'local') {
        const ollamaUrl = aiSettings.localUrl || 'http://localhost:11434';
        const model = aiSettings.localModel || 'llama3';

        let systemPrompt = "You are an AI Mentor inside a collaborative code editor. Answer the user's questions clearly and concisely. Format code blocks properly.";
        let userMessageStr = "";
        
        if (readCode && editorContext.code) {
           userMessageStr += `<editor_source_of_truth>\n\`\`\`${language || 'text'}\n${editorContext.code}\n\`\`\`\n</editor_source_of_truth>\n\n`;
        }
        if (readCode && editorContext.selectedCode) {
           userMessageStr += `// User selected code:\n\`\`\`${language || 'text'}\n${editorContext.selectedCode}\n\`\`\`\n\n`;
        }
        userMessageStr += question;

        const ollamaMessages = [
          { role: 'system', content: systemPrompt },
          ...messages.filter(m => !m.isError && m.content).map(m => ({ role: m.role === 'ai' ? 'assistant' : m.role, content: m.content })),
          { role: 'user', content: userMessageStr }
        ];

        const response = await fetch(`${ollamaUrl.replace(/\/+$/, '')}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: model,
            messages: ollamaMessages,
            stream: true
          }),
          signal: abortControllerRef.current.signal
        });

        if (!response.ok) {
          const errText = await response.text();
          let errMsg = errText;
          try {
            const parsed = JSON.parse(errText);
            if (parsed.error) errMsg = parsed.error;
          } catch(e) {}
          throw new Error(`Ollama Error (${response.status}): ${errMsg || 'Ensure Ollama is running and CORS is enabled.'}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let done = false;
        let fullText = '';
        let buffer = '';
        let lastUpdateTime = Date.now();

        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;
          if (value) {
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); 
            let chunkText = '';
            for (const line of lines) {
              if (!line.trim()) continue;
              try {
                const data = JSON.parse(line);
                if (data.message && data.message.content) {
                  chunkText += data.message.content;
                }
              } catch (e) {}
            }
            if (chunkText) {
              fullText += chunkText;
              const now = Date.now();
              if (now - lastUpdateTime > 50) {
                 setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, content: fullText } : m));
                 lastUpdateTime = now;
              }
            }
          }
        }
        
        setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, content: fullText } : m));
        speakText(fullText);

        try {
          if (token && roomId) {
            await fetch('/api/ai/history', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ roomId, question, answer: fullText })
            });
          }
        } catch (e) {
          console.error('Failed to sync history to backend', e);
        }

      } else {
        const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question,
          editorCode: readCode ? editorContext.code : '',
          selectedCode: readCode ? editorContext.selectedCode : '',
          cursorPosition: readCode ? editorContext.cursorLine : null,
          language,
          roomId,
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI response.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let fullText = '';
      let buffer = '';
      let lastUpdateTime = Date.now();

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep last incomplete line in buffer
          let chunkText = '';
          for (const line of lines) {
            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  chunkText += data.content;
                }
              } catch (e) { }
            }
          }

          if (chunkText) {
            fullText += chunkText;
            
            // Batch UI updates every 50ms to prevent browser freeze (Page Unresponsive)
            const now = Date.now();
            if (now - lastUpdateTime > 50) {
               setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, content: fullText } : m));
               lastUpdateTime = now;
            }
          }
        }
      }

      // Final forced update to ensure the last chunk is rendered
      setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, content: fullText } : m));

      speakText(fullText);
      } // End of cloud else block
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('AI generation cancelled');
      } else {
        console.error(error);
        toast.error('AI Mentor Error: ' + error.message);
        const errorMsg = { role: 'ai', content: 'Sorry, I encountered an error. Please try again.', id: Date.now() + 2, isError: true };
        setMessages(prev => [...prev, errorMsg]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const hasMessages = messages.length > 0;
  const lastMessageExists = messages.some(m => m.role === 'ai');

  return (
    <div className="chatPanel mentorPanel" style={{ width: width ? `${width}px` : undefined }}>
      <MentorHeader onClose={onClose} onOpenSettings={() => setShowSettings(true)} />

      {showSettings && (
        <MentorSettingsModal 
          onClose={() => setShowSettings(false)}
          currentSettings={aiSettings}
          onSave={(newSettings) => {
            setAiSettings(newSettings);
            localStorage.setItem('aiMentorSettings', JSON.stringify(newSettings));
          }}
        />
      )}

      <div className="chatMessages mentorMessages">
        {!hasMessages && (
          <div className="systemMessage">
            Hi! I'm your AI Mentor. Ask me a question about your code. I'll read your current editor content automatically.
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        <TypingIndicator isTyping={isLoading} />
        <div ref={messagesEndRef} />
      </div>

      <div className="mentorInputArea">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: '8px' }}>
          <VoiceControls
            isSpeaking={isSpeaking}
            stopSpeaking={stopSpeaking}
            replayLastMessage={replayLastMessage}
            lastMessageExists={lastMessageExists}
          />
        </div>
        <ChatInput 
          onSend={handleSendMessage} 
          isLoading={isLoading} 
          onStop={stopGenerating}
          readCode={readCode}
          onReadCodeChange={setReadCode}
        />
      </div>
    </div>
  );
};

export default AIMentorPanel;
